// Direct port of ~/Desktop/calyx-erp/src/components/deals/DealSubRow/AddInvoiceForm.tsx
// adapted to the calyx-tool React Query mutation surface (createManualInvoice)
// and the project's ProductPicker which receives `products` as a prop.

import React, { useState } from 'react'

import type { Deal } from '../types/deal'
import type { InvoiceItemInput } from '../types/invoice'
import { useToast } from '../../../hooks/useToast'
import { useCreateManualInvoiceMutation } from '../hooks/useCreateManualInvoiceMutation'
import { useProductsQuery } from '../hooks/useProductsQuery'
import { findProductItem, ProductPicker } from '../modals/ProductPicker'
import { useModal } from '../state/ModalProvider'
import { formatCents } from '../utils/formatCents'
import { billingTypeFor } from '../utils/billingType'

interface Props {
  deal: Deal
  onClose: () => void
}

type Mode = 'onetime' | 'prorated'

interface DraftRow {
  id: string
  productValue: string
  qty: number
}

let __seq = 0
const newId = (): string => `row-${++__seq}`

function makeBlankRow(): DraftRow {
  return { id: newId(), productValue: '', qty: 1 }
}

function todayISO(): string {
  const date = new Date()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

function addMonthsISO(iso: string, months: number): string {
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return iso
  const [, yyyy, mm, dd] = match
  const base = new Date(Number(yyyy), Number(mm) - 1, Number(dd))
  base.setMonth(base.getMonth() + months)
  const out = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-${String(base.getDate()).padStart(2, '0')}`
  return out
}

function isoToMDY(iso: string): string {
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return ''
  const [, yyyy, mm, dd] = match
  return `${mm}/${dd}/${yyyy}`
}

export const AddInvoiceForm: React.FunctionComponent<Props> = (props) => {
  const { deal, onClose } = props
  const toast = useToast()
  const { openSendInvoice } = useModal()
  const productsQuery = useProductsQuery()
  const productGroups = productsQuery.data ?? []
  const createMutation = useCreateManualInvoiceMutation()

  const [mode, setMode] = useState<Mode>('onetime')
  const [months, setMonths] = useState(5)
  const [note, setNote] = useState('')
  const [rows, setRows] = useState<DraftRow[]>([makeBlankRow()])
  const [pickerOpenId, setPickerOpenId] = useState<string | null>(null)

  function updateRow(id: string, patch: Partial<DraftRow>): void {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)))
  }

  function removeRow(id: string): void {
    setRows((current) => (current.length === 1 ? current : current.filter((row) => row.id !== id)))
  }

  function addRow(): void {
    setRows((current) => [...current, makeBlankRow()])
  }

  const items: InvoiceItemInput[] = rows
    .filter((row) => row.productValue && row.qty > 0)
    .map((row) => {
      const product = findProductItem(productGroups, row.productValue)
      if (!product) {
        return {
          productKey: row.productValue,
          productName: row.productValue,
          qty: row.qty,
          rateCents: 0,
          discountPercent: 0,
        }
      }
      return {
        productKey: row.productValue,
        productName: product.name,
        qty: row.qty,
        rateCents: product.priceCents,
        discountPercent: 0,
        sub: product.sub,
        isOTP: product.sub === 'otp',
      }
    })

  // Subtotal preview only — backend recomputes amountCents authoritatively.
  // Pro-rated mode scales by product billing cadence so the preview matches
  // the eventual server total instead of showing the raw annual/monthly rate.
  const subtotalCents = rows
    .filter((row) => row.productValue && row.qty > 0)
    .reduce((sum, row) => {
      const product = findProductItem(productGroups, row.productValue)
      const rate = product?.priceCents ?? 0
      const baseCents = rate * row.qty
      if (mode === 'onetime' || !product) {
        return sum + Math.round(baseCents)
      }
      const billing = billingTypeFor(product)
      if (billing === 'annual') return sum + Math.round((baseCents * months) / 12)
      if (billing === 'monthly') return sum + Math.round(baseCents * months)
      return sum + Math.round(baseCents)
    }, 0)
  const canSubmit = items.length > 0 && !createMutation.isLoading

  function buildPeriod(): { startDate: string; endDate: string; label: string } {
    const start = todayISO()
    if (mode === 'prorated') {
      const end = addMonthsISO(start, months)
      return {
        startDate: start,
        endDate: end,
        label: `Pro-rated — ${months} months from ${isoToMDY(start)}`,
      }
    }
    return {
      startDate: start,
      endDate: start,
      label: `${isoToMDY(start)} — One-time purchase`,
    }
  }

  function commitDraft(action: 'close' | 'send'): void {
    if (!canSubmit) {
      toast.current?.show({
        severity: 'error',
        summary: 'Add at least one product line',
        detail: 'Pick a product before saving the invoice.',
        sticky: true,
      })
      return
    }
    const period = buildPeriod()
    createMutation.mutate(
      {
        dealId: deal.id,
        body: {
          status: 'Draft',
          addonType: mode,
          periodStartDate: period.startDate,
          periodEndDate: period.endDate,
          periodLabel: period.label,
          items,
          note: note || undefined,
        },
      },
      {
        onSuccess: (invoice) => {
          onClose()
          if (action === 'send') openSendInvoice(deal.id, invoice.id)
        },
      },
    )
  }

  return (
    <div
      className="addon-inline-form"
      style={{
        marginTop: 8,
        padding: 16,
        background: '#fafafa',
        border: '1px solid #e5e5ea',
        borderRadius: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <strong style={{ fontSize: 13 }}>Manually add invoice</strong>
        <button
          type="button"
          onClick={onClose}
          style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 16 }}
        >
          ×
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button
          type="button"
          onClick={() => setMode('onetime')}
          style={{
            flex: 1,
            padding: '8px 12px',
            borderRadius: 6,
            border: `1px solid ${mode === 'onetime' ? '#1d1d1f' : '#e2e8f0'}`,
            background: mode === 'onetime' ? '#1d1d1f' : 'white',
            color: mode === 'onetime' ? 'white' : '#475569',
            cursor: 'pointer',
            fontSize: 12,
            textAlign: 'left',
          }}
        >
          <div style={{ fontWeight: 600 }}>One-time</div>
          <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>Charged in full at the listed price.</div>
        </button>
        <button
          type="button"
          onClick={() => setMode('prorated')}
          style={{
            flex: 1,
            padding: '8px 12px',
            borderRadius: 6,
            border: `1px solid ${mode === 'prorated' ? '#1d1d1f' : '#e2e8f0'}`,
            background: mode === 'prorated' ? '#1d1d1f' : 'white',
            color: mode === 'prorated' ? 'white' : '#475569',
            cursor: 'pointer',
            fontSize: 12,
            textAlign: 'left',
          }}
        >
          <div style={{ fontWeight: 600 }}>Pro-rated</div>
          <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>Charge only for the remaining months.</div>
        </button>
      </div>

      {mode === 'prorated' ? (
        <div style={{ marginBottom: 12, fontSize: 12, color: '#475569' }}>
          <label style={{ marginRight: 8 }}>Months remaining:</label>
          <input
            type="number"
            min={1}
            max={12}
            value={months}
            onChange={(event) =>
              setMonths(Math.max(1, Math.min(12, Number(event.target.value) || 1)))
            }
            style={{
              width: 64,
              height: 28,
              border: '1px solid #e5e5ea',
              borderRadius: 4,
              padding: '0 6px',
              fontSize: 12,
              fontFamily: 'inherit',
            }}
          />
        </div>
      ) : null}

      <div style={{ marginBottom: 12 }}>
        {rows.map((row) => (
          <div
            key={row.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6,
            }}
          >
            <div style={{ flex: 1, minWidth: 220 }}>
              <ProductPicker
                value={row.productValue}
                onChange={(value) => updateRow(row.id, { productValue: value })}
                isOpen={pickerOpenId === row.id}
                onOpenChange={(open) => setPickerOpenId(open ? row.id : null)}
                products={productGroups}
              />
            </div>
            <input
              type="number"
              min={1}
              value={row.qty}
              onChange={(event) =>
                updateRow(row.id, { qty: Math.max(1, Number(event.target.value) || 1) })
              }
              style={{
                width: 64,
                height: 32,
                border: '1px solid #e5e5ea',
                borderRadius: 6,
                padding: '0 8px',
                fontSize: 12,
                textAlign: 'right',
                fontFamily: 'inherit',
              }}
            />
            <button
              type="button"
              onClick={() => removeRow(row.id)}
              disabled={rows.length === 1}
              style={{
                background: 'transparent',
                border: 'none',
                color: rows.length === 1 ? '#cbd5e1' : '#94a3b8',
                cursor: rows.length === 1 ? 'not-allowed' : 'pointer',
                padding: 4,
                fontSize: 16,
                lineHeight: 1,
              }}
              title="Remove line"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addRow}
          style={{
            fontSize: 12,
            color: '#475569',
            background: 'transparent',
            border: '1px dashed #cbd5e1',
            borderRadius: 6,
            padding: '6px 10px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            marginTop: 4,
          }}
        >
          + Add product
        </button>
      </div>

      <textarea
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder="Optional notes (shown on the invoice)"
        rows={2}
        style={{
          width: '100%',
          padding: '8px 10px',
          fontSize: 12,
          border: '1px solid #e5e5ea',
          borderRadius: 6,
          fontFamily: 'inherit',
          resize: 'vertical',
          marginBottom: 12,
        }}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13, color: '#1d1d1f' }}>
          Subtotal: <strong>{formatCents(subtotalCents)}</strong>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 14px',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              background: 'white',
              color: '#475569',
              fontSize: 12,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => commitDraft('close')}
            disabled={!canSubmit}
            style={{
              padding: '8px 14px',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              background: 'white',
              color: canSubmit ? '#1d1d1f' : '#94a3b8',
              fontSize: 12,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
              fontWeight: 600,
            }}
          >
            Save as draft
          </button>
          <button
            type="button"
            onClick={() => commitDraft('send')}
            disabled={!canSubmit}
            style={{
              padding: '8px 14px',
              border: '1px solid #1d1d1f',
              borderRadius: 6,
              background: canSubmit ? '#1d1d1f' : '#cbd5e1',
              color: 'white',
              fontSize: 12,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
              fontWeight: 600,
            }}
          >
            Save &amp; Send
          </button>
        </div>
      </div>
    </div>
  )
}
